<script>
/**
 * ConfirmDialog -- the canonical guard for risky actions.
 *
 * A thin wrapper over Bootstrap-Vue's <b-modal>, which natively provides the
 * focus-trap, ESC/backdrop dismissal, and return-focus-to-trigger behaviour
 * (AC-5) -- we wire it, we do not re-implement it.
 *
 * The caller MUST supply a descriptive `title` (e.g. "Ship order #1234 via
 * BobGo?"); there is deliberately no generic "Are you sure?" default (AC-4).
 *
 * OPEN IT VIA the `v-b-modal.<id>` directive (or `this.$bvModal.show(id)`) from
 * the triggering element -- that is the single supported open path, so focus
 * returns to the trigger automatically. (There is intentionally no v-model /
 * `:visible` path: binding it fights the directive and loses return-focus.)
 *
 * Event contract: `@confirm` fires on the positive action; `@cancel` fires on
 * EVERY dismissal (Cancel button, ESC, backdrop, header ✕); `@hide` is the raw
 * lifecycle event (cancelable BvModalEvent) for consumers that need it.
 */
const VARIANTS = ["primary", "danger"];

export default {
  name: "ConfirmDialog",
  props: {
    id: {
      type: String,
      default: "confirm-dialog"
    },
    title: {
      type: String,
      required: true
    },
    body: {
      type: String,
      default: ""
    },
    confirmLabel: {
      type: String,
      default: "Confirm"
    },
    variant: {
      type: String,
      default: "primary",
      validator: value => VARIANTS.indexOf(value) !== -1
    }
  },
  data() {
    return {
      // Double-submit guard: once Confirm fires we block further confirms until
      // the dialog is reopened (b-modal fades out asynchronously, so the button
      // stays clickable during the transition).
      submitting: false
    };
  },
  methods: {
    onConfirm() {
      if (this.submitting) {
        return;
      }
      this.submitting = true;
      this.$emit("confirm");
      this.$refs.modal.hide("confirm");
    },
    onCancelClick() {
      // Route the Cancel button through hide() so every dismissal path funnels
      // through onHide and emits a single, symmetric `cancel`.
      this.$refs.modal.hide("cancel");
    },
    onHide(evt) {
      this.$emit("hide", evt);
      // Symmetric intent contract: any non-confirm dismissal (Cancel, ESC,
      // backdrop, header ✕) emits `cancel` so consumers can wire @confirm/@cancel.
      if (evt.trigger !== "confirm") {
        this.$emit("cancel");
      }
    },
    onShown() {
      this.submitting = false;
      // For destructive dialogs land focus on Cancel (never the destructive
      // button) so a stray Enter/Space cannot fire the dangerous action. (AC-7)
      const target =
        this.variant === "danger" ? this.$refs.cancelBtn : this.$refs.confirmBtn;
      if (target && target.$el) {
        target.$el.focus();
      }
    }
  }
};
</script>

<template>
  <b-modal
    ref="modal"
    :id="id"
    :title="title"
    centered
    @hide="onHide"
    @shown="onShown"
  >
    <slot>
      <p class="confirm-dialog__body mb-0">{{ body }}</p>
    </slot>

    <template #modal-footer>
      <b-button
        ref="cancelBtn"
        variant="link"
        class="confirm-dialog__btn"
        @click="onCancelClick"
      >Cancel</b-button>
      <b-button
        ref="confirmBtn"
        :variant="variant"
        class="confirm-dialog__btn"
        :disabled="submitting"
        @click="onConfirm"
      >{{ confirmLabel }}</b-button>
    </template>
  </b-modal>
</template>

<style scoped>
.confirm-dialog__body {
  font-size: var(--t-body);
  color: var(--c-text);
}

/* Hit targets >=40px for tablet tapping (UX-DR16). */
.confirm-dialog__btn {
  min-height: 40px;
}

/* Visible 2px primary focus ring on both buttons (AC-7).
   Uses the shared --focus-ring token (Story 1.6) — one source of truth. */
.confirm-dialog__btn:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}
</style>
